import { AppText } from '../../components/AppText';
import { RetryNotice } from '../../components/RetryNotice';
import { PrimaryButton } from '../../components/PrimaryButton';
import { Strings } from '../../strings';
import { SymbolView } from '../../symbols/SymbolView';

import { useChildHome } from './useChildHome';

/**
 * design-system/08-Screens.md §2 — one hub, one obvious next action.
 * Mirrors android/…/Screens/ChildHome/ChildHomeScreen.kt.
 *
 * No tabs, no drawer, no bottom bar. A child's home has exactly one thing to do, and everything else on it
 * is a reward for having done it.
 */
export function ChildHomeScreen({
  nickname,
  onStartTraining,
  onOpenIsland,
  onOpenAchievements,
}: {
  nickname: string;
  onStartTraining: () => void;
  onOpenIsland: () => void;
  onOpenAchievements: () => void;
}) {
  const model = useChildHome(nickname);
  const island = model.state.island;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <AppText as="h1" style="titleL">
        {Strings.childHomeGreeting(model.state.nickname)}
      </AppText>

      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Counter
          token="star"
          label={Strings.childStars}
          value={island?.starBalance ?? 0}
          color="var(--reward-star)"
        />
        <Counter
          token="circle"
          label={Strings.childCoins}
          value={island?.coinBalance ?? 0}
          color="var(--reward-coin)"
        />
      </div>

      {/* Never `error` in child mode — there is no red on a child's screen (02-Colour §2). */}
      {model.state.error !== null ? (
        <RetryNotice text={model.state.error} tone="info" onRetry={() => void model.load()} />
      ) : null}

      <div
        style={{
          borderRadius: 'var(--radius-l)',
          background: 'var(--surface-raised)',
          padding: 'var(--space-5)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-4)',
        }}
      >
        <AppText style="titleM">{Strings.childHomeToday}</AppText>

        {/*
          An unfinished training is not lost — `startDaily` hands the same session back — but nothing said
          so, so leaving one looked like throwing it away (R0077). The button says continue, and the line
          below says how far through.
        */}
        {model.state.today?.status === 'completed' ? (
          <AppText style="bodyS" color="var(--text-secondary)">
            {Strings.childHomeDoneToday}
          </AppText>
        ) : (
          <>
            <PrimaryButton
              label={
                model.state.today?.status === 'in_progress'
                  ? Strings.childHomeContinue
                  : Strings.actionStartTraining
              }
              onClick={onStartTraining}
            />
            {model.state.today?.status === 'in_progress' ? (
              <AppText style="bodyS" color="var(--text-secondary)">
                {Strings.childHomeUnfinished(
                  String(model.state.today.completedCount),
                  String(model.state.today.plannedCount),
                )}
              </AppText>
            ) : null}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={onOpenIsland}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          borderRadius: 'var(--radius-l)',
          background: 'var(--surface-reward)',
          border: 'none',
          padding: 'var(--space-5)',
          textAlign: 'left',
        }}
      >
        <SymbolView token="house" size={48} color="var(--action-primary)" />
        <AppText as="span" style="titleS">
          {Strings.childHomeIsland}
        </AppText>
      </button>

      <button
        type="button"
        onClick={onOpenAchievements}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-4)',
          borderRadius: 'var(--radius-l)',
          background: 'var(--surface-raised)',
          border: 'none',
          padding: 'var(--space-5)',
          textAlign: 'left',
        }}
      >
        <SymbolView token="flag" size={48} color="var(--action-primary)" />
        <AppText as="span" style="titleS">
          {Strings.childHomeAchievements}
        </AppText>
      </button>
    </div>
  );
}

function Counter({
  token,
  label,
  value,
  color,
}: {
  token: string;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        borderRadius: 'var(--radius-m)',
        background: 'var(--surface-raised)',
        padding: 'var(--space-4)',
      }}
    >
      <SymbolView token={token} size={28} color={color} />

      <div>
        <AppText style="titleM" color={color}>
          {value}
        </AppText>
        <AppText style="bodyS" color="var(--text-secondary)">
          {label}
        </AppText>
      </div>
    </div>
  );
}
